import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ExternalLink } from 'lucide-react';

const SESSION_KEY = 'docubot_proactive_shown_';

/**
 * Proactive messages popup — appears near the closed chat launcher.
 * Mirrors Gleap/Juvonno-style welcome popups.
 *
 * Each message: {
 *   id, type: 'text'|'card', enabled,
 *   delay_seconds, trigger: 'first_visit'|'every_load'|'after_inactivity'|'after_delay',
 *   show_once_per_session,
 *   message, avatar_initial, sender_name,
 *   card_title, card_emoji, card_description, card_button_label, card_button_url
 * }
 */
const ProactiveMessages = ({ subdomain, primaryColor, messages = [], onOpenWidget, onOpenForm, onOpenBooking }) => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [activeMessages, setActiveMessages] = useState([]);

  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const enabledMsgs = messages.filter(m => m.enabled !== false);
    if (enabledMsgs.length === 0) return;

    // Schedule each message based on its trigger
    const timers = [];
    enabledMsgs.forEach((msg) => {
      const sessionKey = SESSION_KEY + subdomain + '_' + msg.id;
      const trigger = msg.trigger || 'after_delay';
      const showOnce = msg.show_once_per_session !== false;
      const delayMs = (parseInt(msg.delay_seconds) || 3) * 1000;

      if (showOnce && sessionStorage.getItem(sessionKey)) return;

      if (trigger === 'first_visit') {
        const visitKey = 'docubot_visited_' + subdomain;
        if (localStorage.getItem(visitKey)) return;
        localStorage.setItem(visitKey, '1');
        timers.push(setTimeout(() => showMessage(msg, sessionKey), delayMs));
      } else if (trigger === 'every_load') {
        timers.push(setTimeout(() => showMessage(msg, sessionKey), delayMs));
      } else if (trigger === 'after_inactivity') {
        let inactivityTimer = setTimeout(() => showMessage(msg, sessionKey), delayMs);
        const resetTimer = () => {
          clearTimeout(inactivityTimer);
          inactivityTimer = setTimeout(() => showMessage(msg, sessionKey), delayMs);
        };
        ['mousemove', 'keydown', 'scroll'].forEach(e => window.addEventListener(e, resetTimer));
        timers.push(() => {
          clearTimeout(inactivityTimer);
          ['mousemove', 'keydown', 'scroll'].forEach(e => window.removeEventListener(e, resetTimer));
        });
      } else {
        // after_delay
        timers.push(setTimeout(() => showMessage(msg, sessionKey), delayMs));
      }
    });

    return () => timers.forEach(t => { if (typeof t === 'function') t(); else clearTimeout(t); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, subdomain]);

  const showMessage = (msg, sessionKey) => {
    if (dismissed) return;
    setActiveMessages(prev => {
      if (prev.find(m => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
    setVisible(true);
    if (msg.show_once_per_session !== false) sessionStorage.setItem(sessionKey, '1');
  };

  const dismiss = () => { setVisible(false); setDismissed(true); setActiveMessages([]); };

  if (!visible || activeMessages.length === 0) return null;

  return (
    <div className="fixed bottom-24 right-5 z-40 w-[360px] max-w-[calc(100vw-2rem)] space-y-2 animate-in slide-in-from-bottom-4 fade-in duration-300" data-testid="proactive-messages">
      <div className="flex justify-end">
        <button
          onClick={dismiss}
          className="h-7 w-7 rounded-full bg-white shadow-md hover:shadow-lg flex items-center justify-center text-zinc-500 hover:text-zinc-700 transition-all"
          data-testid="proactive-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {activeMessages.map((msg, idx) => (
        <div key={msg.id || idx} className="bg-white rounded-2xl shadow-[0_24px_60px_-12px_rgba(0,0,0,0.18)] p-5 cursor-pointer hover:shadow-[0_24px_60px_-8px_rgba(0,0,0,0.22)] transition-shadow" onClick={msg.type === 'iframe' ? undefined : onOpenWidget} data-testid={`proactive-msg-${idx}`}>
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: primaryColor }}>
              {msg.avatar_initial || (msg.sender_name?.[0] || 'A').toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-zinc-900 text-sm mb-1">{msg.greeting_title || 'Welcome! 👋'}</div>
              {msg.message && <div className="text-sm text-zinc-700 leading-relaxed">{msg.message}</div>}

              {/* Link type */}
              {msg.type === 'link' && msg.link_url && (
                <button
                  onClick={(e) => { e.stopPropagation(); window.open(msg.link_url, '_blank'); }}
                  className="mt-3 w-full h-10 rounded-xl text-sm font-bold text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-1"
                  style={{ backgroundColor: primaryColor }}
                  data-testid={`proactive-link-${idx}`}
                >
                  {msg.link_button_label || 'Learn more'} <ExternalLink className="h-3.5 w-3.5" />
                </button>
              )}

              {/* Calendar type */}
              {msg.type === 'calendar' && msg.booking_url && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenBooking) onOpenBooking(msg.booking_url, msg.button_label || 'Book a time');
                    else window.open(msg.booking_url, '_blank');
                  }}
                  className="mt-3 w-full h-10 rounded-xl text-sm font-bold text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-1"
                  style={{ backgroundColor: primaryColor }}
                  data-testid={`proactive-calendar-${idx}`}
                >
                  📅 {msg.button_label || 'Pick a time'}
                </button>
              )}

              {/* Iframe type */}
              {msg.type === 'iframe' && msg.iframe_url && (
                <div className="mt-3 rounded-xl overflow-hidden border border-zinc-200" onClick={(e) => e.stopPropagation()} data-testid={`proactive-iframe-${idx}`}>
                  <iframe
                    src={msg.iframe_url}
                    title="Embedded"
                    style={{ width: '100%', height: `${msg.iframe_height || 360}px`, border: 0 }}
                    loading="lazy"
                  />
                </div>
              )}

              {/* Form type */}
              {msg.type === 'form' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (msg.form_slug && onOpenForm) onOpenForm(msg.form_slug);
                    else onOpenWidget?.();
                  }}
                  className="mt-3 w-full h-10 rounded-xl text-sm font-bold text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-1"
                  style={{ backgroundColor: primaryColor }}
                  data-testid={`proactive-form-${idx}`}
                >
                  📋 {msg.button_label || 'Open form'}
                </button>
              )}

              {/* Legacy 'card' type — keep backward compatibility */}
              {msg.type === 'card' && (msg.card_title || msg.card_description) && (
                <div className="mt-3 rounded-xl border border-zinc-200 p-3.5 bg-zinc-50/50">
                  {msg.card_title && <div className="font-bold text-zinc-900 text-sm">{msg.card_title}{msg.card_emoji ? ` ${msg.card_emoji}` : ''}</div>}
                  {msg.card_description && <div className="text-xs text-zinc-600 mt-1">{msg.card_description}</div>}
                  {msg.card_button_label && (
                    <button
                      onClick={(e) => { e.stopPropagation(); if (msg.card_button_url) window.open(msg.card_button_url, '_blank'); else onOpenWidget?.(); }}
                      className="mt-3 w-full h-10 rounded-xl text-sm font-bold text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-1"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {msg.card_button_label} <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}

              <div className="text-xs text-zinc-400 mt-2">
                {msg.sender_name || 'Support'} · just now
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProactiveMessages;
